/**
 * @fileoverview API Instances Module
 *
 * This module creates and exports all API instances for the agnostic-platform.
 * It uses the ApiWrapper class from the factory module to create lazy-initialized
 * instances with proper interceptor configuration.
 *
 * Key features:
 * - 12 pre-configured API instances for different backend services
 * - Centralized header configuration functions
 * - Cache invalidation support for header updates
 * - Factory initialization with header update function
 *
 * @module services/api/instances
 */

import { API_CONFIG, validateEnvVariables } from './config';
import { ApiWrapper, setUpdateHeadersFunction } from './factory';
import { logger } from 'utils/logger';

// ============================================================================
// API Instances
// ============================================================================

/**
 * Main application API client
 *
 * Used for core application endpoints. Base URL from REACT_APP_BASE_URL.
 *
 * @example
 * const data = await apiMain.get('/dashboard');
 */
export const apiMain = new ApiWrapper('main', API_CONFIG.main);

/**
 * User/CAND Core API client
 *
 * Used for user management and CAND-specific operations.
 *
 * @example
 * const profile = await apiUser.get('/profile');
 */
export const apiUser = new ApiWrapper('user', API_CONFIG.user);

/**
 * Rainmakers V2 API client
 *
 * Used for Rainmakers platform features.
 *
 * @example
 * const opportunities = await apiRainmakersV2.get('/opportunities');
 */
export const apiRainmakersV2 = new ApiWrapper('rainmakersV2', API_CONFIG.rainmakersV2);

/**
 * Advisor service API client
 *
 * Used for advisor-related features and recommendations.
 *
 * @example
 * const recommendations = await apiAdvisor.get('/recommendations');
 */
export const apiAdvisor = new ApiWrapper('advisor', API_CONFIG.advisor);

/**
 * Meeting/video service API client
 *
 * Used for video conferencing and meeting management.
 *
 * @example
 * const meeting = await apiMeeting.post('/rooms', { name: 'Team Standup' });
 */
export const apiMeeting = new ApiWrapper('meeting', API_CONFIG.meeting);

/**
 * Authentication orchestrator API client
 *
 * **PRIMARY AUTHENTICATION API** - Used for login, logout, token refresh,
 * and all authentication-related operations.
 *
 * This instance has `withCredentials: true` enabled for cookie-based
 * authentication (access_token and refresh_token cookies).
 *
 * @example
 * // Login
 * const { data } = await apiOrchestrator.post('/auth/login', {
 *   email: 'user@example.com',
 *   password: 'password123'
 * });
 *
 * @example
 * // Refresh token
 * const { data } = await apiOrchestrator.post('/auth/refresh-token');
 *
 * @example
 * // Logout
 * await apiOrchestrator.post('/auth/logout');
 */
export const apiOrchestrator = new ApiWrapper('orchestrator', API_CONFIG.orchestrator);

/**
 * AI Copilot service API client
 *
 * Used for AI-powered features and copilot interactions.
 *
 * @example
 * const suggestion = await apiCopilot.post('/suggest', { context: 'user query' });
 */
export const apiCopilot = new ApiWrapper('copilot', API_CONFIG.copilot);

/**
 * Networking features API client
 *
 * Used for networking, connections, and social features.
 *
 * @example
 * const connections = await apiNetworking.get('/connections');
 */
export const apiNetworking = new ApiWrapper('networking', API_CONFIG.networking);

/**
 * Power BI integration API client
 *
 * Used for dashboard and analytics features powered by Power BI.
 *
 * @example
 * const report = await apiPBI.get('/reports/123');
 */
export const apiPBI = new ApiWrapper('pbi', API_CONFIG.pbi);

/**
 * Dynamic forms API client
 *
 * Used for form configuration, submission, and draft management.
 *
 * @example
 * // Get form configuration
 * const form = await apiForms.get('/forms/123');
 *
 * @example
 * // Submit form response
 * await apiForms.post('/forms/123/responses', { answers: {...} });
 */
export const apiForms = new ApiWrapper('forms', API_CONFIG.forms);

/**
 * Notifications service API client
 *
 * Used for notification management and real-time alerts.
 *
 * @example
 * const notifications = await apiNotifications.get('/notifications', { params: { unread: true } });
 */
export const apiNotifications = new ApiWrapper('notifications', API_CONFIG.notifications);

/**
 * Strapi CMS API client
 *
 * Used for content management operations (courses, articles, media, etc.).
 * Requires special header setup via `setupStrapiHeaders()`.
 *
 * @see setupStrapiHeaders - For proper header configuration
 *
 * @example
 * // Ensure headers are configured first
 * setupStrapiHeaders(token, orgUUID, userPool);
 *
 * // Then make requests
 * const courses = await apiStrapi.get('/courses');
 */
export const apiStrapi = new ApiWrapper('strapi', API_CONFIG.strapi);

// ============================================================================
// Header Configuration Functions
// ============================================================================

/**
 * Configures default headers for ALL API instances
 *
 * This function should be called after successful login to propagate
 * authentication headers to all API instances. It also cleans up
 * any undefined headers from previous sessions.
 *
 * ## Headers Set
 * - `Authorization: Bearer {token}` - JWT access token
 * - `X-Organization-UUID: {uuid}` - Organization identifier (if provided)
 * - `X-Cognito-UserPool: {pool}` - Cognito pool ID (if provided)
 *
 * ## When to Call
 * - After successful login
 * - After token refresh
 * - After organization switch
 *
 * @param token - JWT access token (required)
 * @param organizationUUID - Organization UUID (optional)
 * @param userPool - Cognito User Pool ID (optional)
 * @returns void
 *
 * @example
 * // After login
 * const { data } = await apiOrchestrator.post('/auth/login', credentials);
 * defaultsHeadersAxios(data.accessToken, data.organizationUUID, data.userPool);
 *
 * @example
 * // After token refresh
 * const newToken = await RefreshTokenService.refreshAccessToken();
 * defaultsHeadersAxios(newToken, localStorage.getItem('x-organization-uuid'));
 */
export const defaultsHeadersAxios = (
  token: string,
  organizationUUID?: string,
  userPool?: string
): void => {
  const apis = [
    apiMain,
    apiUser,
    apiRainmakersV2,
    apiAdvisor,
    apiMeeting,
    apiOrchestrator,
    apiCopilot,
    apiNetworking,
    apiPBI,
    apiForms,
    apiStrapi,
    apiNotifications,
  ].map(api => api.getInstance());

  apis.forEach(apiInstance => {
    // Remove undefined headers
    Object.keys(apiInstance.defaults.headers.common).forEach(key => {
      if (apiInstance.defaults.headers.common[key] === undefined) {
        delete apiInstance.defaults.headers.common[key];
      }
    });

    // Set new headers
    apiInstance.defaults.headers.common = {
      ...apiInstance.defaults.headers.common,
      Authorization: token ? `Bearer ${token}` : undefined,
    };

    // Add optional headers if provided
    if (organizationUUID) {
      apiInstance.defaults.headers.common['X-Organization-UUID'] = organizationUUID;
    }

    if (userPool) {
      apiInstance.defaults.headers.common['X-Cognito-UserPool'] = userPool;
    }
  });
};

/**
 * Configures headers specifically for the Strapi CMS API instance
 *
 * Unlike `defaultsHeadersAxios`, this function:
 * - Validates all required headers before setting them
 * - Returns a boolean indicating success/failure
 * - Falls back to localStorage values if params are not provided
 * - Logs detailed errors for debugging
 *
 * ## Required Headers
 * - `Authorization: Bearer {token}` - JWT access token
 * - `X-Cognito-UserPool: {pool}` - Cognito pool ID
 * - `X-Organization-UUID: {uuid}` - Organization identifier
 *
 * ## When to Call
 * - Before making any Strapi API calls
 * - After login or organization switch
 * - When Strapi requests fail with 401/403
 *
 * @param token - JWT access token (falls back to localStorage AUTH_TOKEN)
 * @param organizationUUID - Organization UUID (falls back to localStorage)
 * @param userPool - Cognito User Pool ID (falls back to localStorage)
 * @returns `true` if headers were set successfully, `false` if validation failed
 *
 * @example
 * // Basic usage with explicit values
 * const success = setupStrapiHeaders(accessToken, orgUUID, userPool);
 * if (!success) {
 *   console.error('Failed to configure Strapi headers');
 *   return;
 * }
 * const courses = await apiStrapi.get('/courses');
 *
 * @example
 * // Using localStorage fallback
 * const success = setupStrapiHeaders(undefined); // Uses localStorage values
 * if (success) {
 *   const articles = await apiStrapi.get('/articles');
 * }
 */
export const setupStrapiHeaders = (
  token: string | undefined,
  organizationUUID?: string,
  userPool?: string
): boolean => {
  const instance = apiStrapi.getInstance();

  // Get headers from localStorage or params
  const cognitoUserPool = userPool || localStorage.getItem('x-cognito-userpool');
  const orgUUID = organizationUUID || localStorage.getItem('x-organization-uuid');
  const authToken = token || localStorage.getItem('AUTH_TOKEN');

  // Required headers validation with detailed logging
  if (!authToken) {
    logger.error('Strapi API: Token is required');
    return false;
  }

  if (!cognitoUserPool) {
    logger.error('Strapi API: Cognito User Pool is not configured');
    return false;
  }

  if (!orgUUID) {
    logger.error('Strapi API: Organization UUID is required');
    return false;
  }

  try {
    // Reset headers
    instance.defaults.headers.common = {};

    // Set new headers
    instance.defaults.headers.common = {
      Authorization: `Bearer ${authToken}`,
      'X-Cognito-UserPool': cognitoUserPool,
      'X-Organization-UUID': orgUUID,
    };

    // Verify headers were set correctly
    const currentHeaders = instance.defaults.headers.common;
    const requiredHeaders = ['Authorization', 'X-Cognito-UserPool', 'X-Organization-UUID'];
    const missingHeaders = requiredHeaders.filter(key => !currentHeaders[key]);

    if (missingHeaders.length > 0) {
      logger.error('Strapi API: Headers not set correctly:', { missing: missingHeaders });
      return false;
    }

    return true;
  } catch (error) {
    logger.error('Strapi API: Error setting headers:', error);
    return false;
  }
};

/**
 * Invalidates the header cache to force fresh localStorage reads
 *
 * The API service caches authentication headers for 1 minute to minimize
 * localStorage reads and improve performance. This function dispatches
 * a custom event to notify all interceptors that the cache should be
 * invalidated.
 *
 * ## When to Call
 * - After updating AUTH_TOKEN in localStorage
 * - After login/logout operations
 * - After token refresh
 * - After organization switch
 *
 * ## Technical Details
 * - Dispatches 'auth-headers-updated' CustomEvent on window
 * - All request interceptors listen for this event
 * - Cache TTL is 1 minute (60000ms) - this function forces immediate invalidation
 *
 * @returns void
 *
 * @example
 * // After saving new token
 * localStorage.setItem('AUTH_TOKEN', newToken);
 * invalidateHeadersCache(); // Ensures next API call uses new token
 *
 * @example
 * // After logout
 * localStorage.removeItem('AUTH_TOKEN');
 * localStorage.removeItem('x-organization-uuid');
 * invalidateHeadersCache();
 *
 * @example
 * // In login handler
 * async function handleLogin(credentials) {
 *   const { data } = await apiOrchestrator.post('/auth/login', credentials);
 *   localStorage.setItem('AUTH_TOKEN', data.accessToken);
 *   defaultsHeadersAxios(data.accessToken, data.orgUUID, data.userPool);
 *   invalidateHeadersCache(); // Ensure immediate effect
 * }
 */
export const invalidateHeadersCache = (): void => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('auth-headers-updated'));
  }
};

// ============================================================================
// Module Initialization
// ============================================================================

// Set the update headers function in factory to avoid circular dependency
setUpdateHeadersFunction(defaultsHeadersAxios);

// Validate environment variables on module load
validateEnvVariables();
